import React, { useState } from 'react';
import { adminAPI } from '../../services/api';
import { Archive, Loader2 } from 'lucide-react';

// Archives ONE workspace. Unlike ArchiveAccountModal this has a typed-name gate:
// an account has one obvious identity (its owner), but workspaces under an
// account often have near-identical names, and archiving the wrong one silently
// locks its members out.
const ArchiveWorkspaceModal = ({ workspace, isLastLive, onClose, onArchived }) => {
    const [reason, setReason] = useState('');
    const [typedName, setTypedName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const canSubmit = reason.trim() && typedName === workspace.name && !submitting;

    const handleArchive = async () => {
        setSubmitting(true);
        setError('');
        try {
            await adminAPI.archiveWorkspace(workspace.id, { reason: reason.trim() });
            onArchived();
        } catch (err) {
            const violations = err?.response?.data?.violations;
            setError(violations?.length
                ? violations.map((v) => v.message).join(' ')
                : err?.response?.data?.message || 'Could not archive this workspace.');
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-lg border border-border bg-card p-5 shadow-lg">
                <div className="mb-4 flex items-start gap-3">
                    <Archive className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                    <div>
                        <h2 className="text-lg font-semibold text-foreground">Archive workspace</h2>
                        <p className="text-sm text-muted-foreground">
                            Freezes {workspace.name}. Its members lose access, and its scheduled runs and
                            alerts stop. Billing is not touched. Nothing is deleted &mdash; this can be restored later.
                        </p>
                    </div>
                </div>

                {isLastLive && (
                    <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">
                        This is the account's last live workspace. Its subscription keeps billing &mdash;
                        archive the account instead if the customer is leaving.
                    </div>
                )}

                {error && (
                    <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {error}
                    </div>
                )}

                <label className="mb-1 block text-sm font-medium text-foreground" htmlFor="ws-archive-reason">Reason</label>
                <input id="ws-archive-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                       className="mb-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />

                <label className="mb-1 block text-sm font-medium text-foreground" htmlFor="ws-archive-name">
                    Type the workspace name to confirm
                </label>
                <input id="ws-archive-name" value={typedName} onChange={(e) => setTypedName(e.target.value)}
                       placeholder={workspace.name}
                       className="mb-4 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />

                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose}
                            className="rounded-md border border-border px-3 py-1.5 text-sm">Cancel</button>
                    <button type="button" onClick={handleArchive} disabled={!canSubmit}
                            className="inline-flex items-center gap-1 rounded-md bg-destructive px-3 py-1.5 text-sm text-destructive-foreground disabled:opacity-50">
                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        Archive workspace
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ArchiveWorkspaceModal;
